import { z } from 'zod';
import { pollerRun } from './poller-runs';

/**
 * Per-user email-poller RUN DETAILS (#316). Operator-facing drill-down that EXTENDS the
 * GLOBAL aggregate poller history (`poller_run`, #306) with per-user, per-email DETAIL so an
 * operator can troubleshoot user reports ("why didn't my agent reply to X", "an email never
 * showed up"). Three read surfaces, all operator-gated (never reachable per workspace):
 *
 *   GET /api/admin/poller-runs/:id                      → `pollerRunDetailResponse`
 *   GET /api/admin/poller-decisions?…filters            → `pollerDecisionsResponse`
 *   GET /api/admin/poller-connections/:id/health        → `pollerConnectionHealthResponse`
 *
 * Unlike the GLOBAL `poller_run` (no PII, I6), these rows are sourced from tenant-owned tables
 * with the SAME sensitivity as `events` — subject / from-address ARE carried, because they are
 * exactly what the operator needs to find "my email". PII stays behind the operator gate.
 */

/** Default / max page size for the decisions lookup (mirrors the poller-runs list clamp). */
export const POLLER_DECISIONS_DEFAULT_LIMIT = 50;
export const POLLER_DECISIONS_MAX_LIMIT = 200;
/** Recent per-connection rollups returned by the connection-health drill-down. */
export const POLLER_CONNECTION_HEALTH_DEFAULT_LIMIT = 50;
export const POLLER_CONNECTION_HEALTH_MAX_LIMIT = 200;

/**
 * What the poller DID with one ingested email (§12.2):
 *  - `spawned_run`        — a trigger matched → a NEW run was created (`runId` set).
 *  - `resumed_run`        — a parked WAITING run matched its resume-matcher → resumed/woken (`runId` set).
 *  - `suppressed_self`    — the #73 self/non-actionable inbound filter blocked it (the account's own mail).
 *  - `non_actionable`     — the §17.1 auto-skip filter judged it obvious no-reply mail (`reason` set).
 *  - `no_matching_trigger`— stored, but NO enabled agent trigger subscribes to it (no run possible).
 *  - `duplicate`          — a redelivered event whose run already existed (no second run).
 */
export const pollerEmailDecisionType = z.enum([
  'spawned_run',
  'resumed_run',
  'suppressed_self',
  'non_actionable',
  'no_matching_trigger',
  'duplicate',
]);
export type PollerEmailDecisionType = z.infer<typeof pollerEmailDecisionType>;

/** The §17.1 `classifyNonActionable` reason carried when `decision = non_actionable`. */
export const pollerNonActionableReason = z.enum([
  'noreply-sender',
  'calendar-auto',
  'list-mail',
  'auto-submitted',
  'bulk-precedence',
  'delivery-report',
]);
export type PollerNonActionableReason = z.infer<typeof pollerNonActionableReason>;

/** The #73 self/actionable sub-reason carried when `decision = suppressed_self` (#328). */
export const pollerSelfSuppressReason = z.enum(['self-sent', 'not-in-inbox']);
export type PollerSelfSuppressReason = z.infer<typeof pollerSelfSuppressReason>;

/** One per-email decision row (newest-first in the lookup). */
export const pollerEmailDecision = z.object({
  id: z.string(),
  workspaceId: z.string(),
  connectionId: z.string().nullable(),
  /** Correlates to the `poller_run` tick; null for the non-poller ingest path. */
  pollerRunId: z.string().nullable(),
  eventId: z.string().nullable(),
  messageId: z.string().nullable(),
  threadId: z.string().nullable(),
  fromAddress: z.string().nullable(),
  subject: z.string().nullable(),
  /** ISO-8601 UTC instant the email was received (the event's `occurredAt`). */
  occurredAt: z.string().datetime(),
  decision: pollerEmailDecisionType,
  /**
   * The classifier reason when `decision = non_actionable`, or the #328 self/actionable
   * sub-reason when `decision = suppressed_self`; null for the other decisions.
   */
  reason: z.union([pollerNonActionableReason, pollerSelfSuppressReason]).nullable(),
  /** The run spawned/resumed by this decision; null for suppress / no-match / duplicate. */
  runId: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type PollerEmailDecision = z.infer<typeof pollerEmailDecision>;

/** One per-connection fetch-diagnostics rollup for a poller tick. */
export const pollerRunConnection = z.object({
  id: z.string(),
  workspaceId: z.string(),
  connectionId: z.string(),
  pollerRunId: z.string().nullable(),
  /** Events emitted by the connector poll (before dedupe). */
  polled: z.number().int().nonnegative(),
  /** Events newly stored (post-dedupe) — the ones dispatched. */
  stored: z.number().int().nonnegative(),
  /** Runs spawned from this connection's stored emails this tick. */
  runsSpawned: z.number().int().nonnegative(),
  /** Adaptive backoff multiplier AFTER this poll (1 = base cadence; grows when quiet). */
  backoffMultiplier: z.number().int().positive(),
  /** ISO-8601 UTC instant this connection is next due to poll, or null. */
  nextPollAt: z.string().datetime().nullable(),
  /** Per-connection poll error (`String(err)`); null on success. */
  error: z.string().nullable(),
  /** ISO-8601 UTC instant of the tick this rollup belongs to. */
  occurredAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type PollerRunConnection = z.infer<typeof pollerRunConnection>;

/** `GET /api/admin/poller-runs/:id`: the tick + its per-connection breakdown + that tick's decisions. */
export const pollerRunDetailResponse = z.object({
  run: pollerRun,
  connections: z.array(pollerRunConnection),
  decisions: z.array(pollerEmailDecision),
});
export type PollerRunDetailResponse = z.infer<typeof pollerRunDetailResponse>;

/** `GET /api/admin/poller-decisions`: the filtered, newest-first per-email lookup. */
export const pollerDecisionsResponse = z.object({
  decisions: z.array(pollerEmailDecision),
});
export type PollerDecisionsResponse = z.infer<typeof pollerDecisionsResponse>;

/** A durable poll cursor position for one (connection, stream) — exposed as-is (no new storage). */
export const pollerConnectionCursor = z.object({
  stream: z.string(),
  cursor: z.string().nullable(),
  /** ISO-8601 UTC instant the cursor last advanced. */
  updatedAt: z.string().datetime(),
});
export type PollerConnectionCursor = z.infer<typeof pollerConnectionCursor>;

/**
 * `GET /api/admin/poller-connections/:connectionId/health`: the cursor position(s) for the
 * connection plus its recent per-tick fetch-diagnostics rollups (newest-first).
 */
export const pollerConnectionHealthResponse = z.object({
  connectionId: z.string(),
  cursors: z.array(pollerConnectionCursor),
  recent: z.array(pollerRunConnection),
});
export type PollerConnectionHealthResponse = z.infer<typeof pollerConnectionHealthResponse>;
